import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { InventoryItems, DEPT_LABEL } from "@/components/InventoryItems";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro, fmtEuroCents, MONTHS_EL } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Save, Lock, Unlock, Trash2, FileSpreadsheet, Search, Scale, Settings2 } from "lucide-react";

const YEAR = new Date().getFullYear();
const field = "rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";

export default function StockCount() {
  const qc = useQueryClient();
  const [year, setYear] = useState(YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [openId, setOpenId] = useState(null);
  const [qty, setQty] = useState({});
  const [q, setQ] = useState("");
  const [cfgOpen, setCfgOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [params] = useSearchParams();
  const dept = params.get("dept") || "fnb";

  const { data: meta } = useQuery({
    queryKey: ["inv-meta", year],
    queryFn: async () => (await api.get(`/inventory/meta?year=${year}`)).data,
  });
  const { data: report } = useQuery({
    queryKey: ["inv-report", year, dept],
    queryFn: async () => (await api.get(`/inventory/report?year=${year}&department=${dept}`)).data,
  });
  const { data: count } = useQuery({
    queryKey: ["inv-count", openId],
    queryFn: async () => (await api.get(`/inventory/counts/${openId}`)).data,
    enabled: !!openId,
  });

  useEffect(() => { if (meta?.purchase_codes) setCodes(meta.purchase_codes); }, [meta]);
  useEffect(() => { setQty({}); }, [openId]);

  const create = useMutation({
    mutationFn: async () => (await api.post("/inventory/counts", { year, month, department: dept })).data,
    onSuccess: (d) => { setOpenId(d.id); qc.invalidateQueries({ queryKey: ["inv-report"] }); toast.success("Δημιουργήθηκε η απογραφή"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const save = useMutation({
    mutationFn: async () => (await api.put(`/inventory/counts/${openId}`, {
      rows: Object.entries(qty).map(([item_id, v]) => ({ item_id, qty: Number(v) || 0 })),
    })).data,
    onSuccess: () => {
      setQty({});
      qc.invalidateQueries({ queryKey: ["inv-count"] });
      qc.invalidateQueries({ queryKey: ["inv-report"] });
      toast.success("Οι ποσότητες αποθηκεύτηκαν");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const act = useMutation({
    mutationFn: async ({ id, what }) => what === "delete"
      ? (await api.delete(`/inventory/counts/${id}`)).data
      : (await api.post(`/inventory/counts/${id}/${what}`)).data,
    onSuccess: (_d, v) => {
      if (v.what === "delete") setOpenId(null);
      qc.invalidateQueries({ queryKey: ["inv-count"] });
      qc.invalidateQueries({ queryKey: ["inv-report"] });
      toast.success("Έγινε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const saveCfg = useMutation({
    mutationFn: async () => (await api.put("/inventory/config", { purchase_codes: codes })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["inv-meta"] }); qc.invalidateQueries({ queryKey: ["inv-report"] }); toast.success("Αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const rows = useMemo(() => {
    const list = count?.rows || [];
    const term = q.trim().toLowerCase();
    return term ? list.filter((r) => r.name.toLowerCase().includes(term) || (r.category || "").toLowerCase().includes(term)) : list;
  }, [count, q]);

  const liveTotal = useMemo(() => (count?.rows || []).reduce((s, r) => {
    const v = qty[r.item_id];
    return s + (v === undefined ? Number(r.qty) : Number(v) || 0) * Number(r.price || 0);
  }, 0), [count, qty]);

  const t = report?.totals || {};
  const locked = count?.status === "closed";

  const downloadSheet = async () => {
    const res = await api.get(`/inventory/counts/${openId}/sheet.xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Apografi_${count.year}_${String(count.month).padStart(2, "0")}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-wide data-testid="stock-count-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">{DEPT_LABEL[dept]} · Απόθεμα</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Μηνιαία Απογραφή · {DEPT_LABEL[dept]}</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Ποσότητα × τελευταία τιμή = αξία αποθέματος. Κόστος πωληθέντων = Αρχικό + Αγορές − Τελικό.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="inv-year" value={year} onChange={(e) => { setYear(Number(e.target.value)); setOpenId(null); }} className={field}>
            {[YEAR + 1, YEAR, YEAR - 1, YEAR - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="inv-config-toggle" onClick={() => setCfgOpen(!cfgOpen)}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold hover:border-[#B9AE9A]">
            <Settings2 className="h-4 w-4" /> Κατηγορίες αγορών
          </button>
        </div>
      </div>

      {cfgOpen && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-3" data-testid="inv-config-panel">
          <p className="text-sm text-[#6F6A60]">
            Επίλεξε ποιες εμπορικές κατηγορίες εξόδων προμηθευτών μετρούν ως <strong>αγορές τροφίμων/ποτών</strong> του μήνα.
          </p>
          <div className="flex flex-wrap gap-2">
            {(meta?.categories || []).map((c) => {
              const on = codes.includes(c.code);
              return (
                <button key={c.code} data-testid={`inv-code-${c.code}`}
                  onClick={() => setCodes(on ? codes.filter((x) => x !== c.code) : [...codes, c.code])}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                    on ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#6F6A60] hover:border-[#B9AE9A]"}`}>
                  {c.code} · {c.label} <span className="opacity-60">({fmtEuro(c.total)})</span>
                </button>
              );
            })}
          </div>
          <button data-testid="inv-config-save" onClick={() => saveCfg.mutate()}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90">
            <Save className="h-4 w-4" /> Αποθήκευση επιλογής
          </button>
        </div>
      )}

      <InventoryItems dept={dept} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="inv-kpis">
        {[
          { k: "Αγορές έτους", v: fmtEuro(t.purchases || 0), id: "kpi-purchases" },
          { k: "Κόστος πωληθέντων", v: fmtEuro(t.cogs || 0), id: "kpi-cogs" },
          { k: "Έσοδα F&B", v: fmtEuro(t.revenue_fb || 0), id: "kpi-revenue" },
          { k: "Food cost", v: t.food_cost_pct === null || t.food_cost_pct === undefined ? "—" : `${t.food_cost_pct}%`, id: "kpi-foodcost" },
        ].filter((c) => dept === "fnb" || ["kpi-cogs"].includes(c.id)).map((c) => (
          <div key={c.id} data-testid={`inv-${c.id}`} className="rounded-2xl bg-white border border-[#E3DED4] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#8A8577] font-semibold">{c.k}</div>
            <div className="font-serif text-2xl text-[#45423C] mt-1 tabular">{c.v}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-3">
          <Scale className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Κόστος πωληθέντων {year}</h2>
          <div className="ml-auto flex items-center gap-2">
            <select data-testid="inv-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className={field}>
              {MONTHS_EL.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <button data-testid="inv-create" onClick={() => create.mutate()} disabled={create.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
              <Plus className="h-4 w-4" /> Νέα απογραφή
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Μήνας</th>
                <th className="px-3 py-3 text-right">Αρχικό</th>
                <th className="px-3 py-3 text-right">Αγορές</th>
                <th className="px-3 py-3 text-right">Τελικό</th>
                <th className="px-3 py-3 text-right">Κόστος πωληθέντων</th>
                <th className="px-3 py-3 text-right">Έσοδα F&amp;B</th>
                <th className="px-3 py-3 text-right">Food cost</th>
                <th className="px-3 py-3 text-center">Απογραφή</th>
              </tr>
            </thead>
            <tbody>
              {(report?.rows || []).map((r) => (
                <tr key={r.month} data-testid={`inv-month-${r.month}`}
                  className={`border-t border-[#E3DED4] ${r.has_data ? "" : "opacity-50"} hover:bg-[#F7F5F1]`}>
                  <td className="px-4 py-2.5 font-medium text-[#45423C]">{MONTHS_EL[r.month]}</td>
                  <td className="px-3 py-2.5 text-right tabular">{fmtEuro(r.opening)}</td>
                  <td className="px-3 py-2.5 text-right tabular">{fmtEuro(r.purchases)}</td>
                  <td className="px-3 py-2.5 text-right tabular">{fmtEuro(r.closing)}</td>
                  <td className="px-3 py-2.5 text-right tabular font-semibold">{r.cogs === null ? "—" : fmtEuro(r.cogs)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-[#6F6A60]">{fmtEuro(r.revenue_fb)}</td>
                  <td className={`px-3 py-2.5 text-right tabular font-semibold ${
                    r.food_cost_pct === null ? "" : r.food_cost_pct <= 32 ? "text-[#4A5A3E]" : "text-[#991B1B]"}`}>
                    {r.food_cost_pct === null ? "—" : `${r.food_cost_pct}%`}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {r.count_id ? (
                      <button data-testid={`inv-open-${r.month}`} onClick={() => setOpenId(r.count_id)}
                        className="rounded-full border border-[#E3DED4] px-3 py-1 text-xs font-semibold hover:border-[#B9AE9A]">
                        {r.status === "closed" ? "Κλειστή" : "Πρόχειρη"}
                      </button>
                    ) : <span className="text-xs text-[#8A8577]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold">
                <td className="px-4 py-3">ΣΥΝΟΛΟ</td>
                <td />
                <td className="px-3 py-3 text-right tabular">{fmtEuro(t.purchases || 0)}</td>
                <td />
                <td className="px-3 py-3 text-right tabular" data-testid="inv-total-cogs">{fmtEuro(t.cogs || 0)}</td>
                <td className="px-3 py-3 text-right tabular">{fmtEuro(t.revenue_fb || 0)}</td>
                <td className="px-3 py-3 text-right tabular">{t.food_cost_pct === null || t.food_cost_pct === undefined ? "—" : `${t.food_cost_pct}%`}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {openId && count && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="inv-count-card">
          <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-3">
            <h2 className="font-medium text-[#45423C]">
              Καταμέτρηση {MONTHS_EL[count.month]} {count.year}
              <span className={`ml-2 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${
                locked ? "bg-[#FEF2F2] text-[#991B1B]" : "bg-[#EDF2E8] text-[#4A5A3E]"}`}>
                {locked ? "κλειστή" : "πρόχειρη"}
              </span>
            </h2>
            <div className="relative">
              <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση υλικού"
                className={`${field} pl-9`} />
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-sm text-[#6F6A60]">Αξία: <strong className="tabular text-[#45423C]" data-testid="inv-live-total">{fmtEuro(liveTotal)}</strong></span>
              <button data-testid="inv-sheet" onClick={downloadSheet}
                className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold hover:border-[#B9AE9A]">
                <FileSpreadsheet className="h-4 w-4" /> Φύλλο Excel
              </button>
              {!locked && (
                <button data-testid="inv-save" onClick={() => save.mutate()} disabled={save.isPending || !Object.keys(qty).length}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                  <Save className="h-4 w-4" /> Αποθήκευση
                </button>
              )}
              <button data-testid="inv-lock" onClick={() => act.mutate({ id: openId, what: locked ? "reopen" : "close" })}
                className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold hover:border-[#B9AE9A]">
                {locked ? <><Unlock className="h-4 w-4" /> Άνοιγμα</> : <><Lock className="h-4 w-4" /> Κλείσιμο μήνα</>}
              </button>
              <button data-testid="inv-delete" onClick={() => { if (window.confirm("Διαγραφή της απογραφής;")) act.mutate({ id: openId, what: "delete" }); }}
                className="p-2 rounded-xl border border-[#E3DED4] text-[#991B1B] hover:bg-[#FEF2F2]"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="md:hidden divide-y divide-[#E3DED4] max-h-[70vh] overflow-y-auto" data-testid="inv-mobile-list">
            {rows.map((r) => {
              const val = qty[r.item_id] === undefined ? r.qty : qty[r.item_id];
              return (
                <div key={r.item_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`inv-mrow-${r.item_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#45423C] truncate">{r.name}</div>
                    <div className="text-[11px] text-[#8A8577]">{r.category || "—"} · {fmtEuroCents(r.price)}/{r.unit}</div>
                  </div>
                  <input data-testid={`inv-mqty-${r.item_id}`} type="number" inputMode="decimal" step="0.01" disabled={locked}
                    value={val} onChange={(e) => setQty({ ...qty, [r.item_id]: e.target.value })}
                    className="w-20 shrink-0 rounded-lg border border-[#E3DED4] px-2 py-2 text-right tabular text-base outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
                  <div className="w-16 shrink-0 text-right text-xs tabular font-semibold text-[#45423C]">{fmtEuroCents((Number(val) || 0) * Number(r.price || 0))}</div>
                </div>
              );
            })}
          </div>
          <div className="hidden md:block overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                  <th className="px-4 py-3">Κατηγορία</th>
                  <th className="px-3 py-3">Υλικό</th>
                  <th className="px-3 py-3 text-center">Μονάδα</th>
                  <th className="px-3 py-3 text-right">Ποσότητα</th>
                  <th className="px-3 py-3 text-right">Τιμή/μονάδα</th>
                  <th className="px-3 py-3 text-right">Αξία</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const val = qty[r.item_id] === undefined ? r.qty : qty[r.item_id];
                  return (
                    <tr key={r.item_id} data-testid={`inv-row-${r.item_id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                      <td className="px-4 py-2 text-xs text-[#6F6A60]">{r.category || "—"}</td>
                      <td className="px-3 py-2 font-medium text-[#45423C]">{r.name}</td>
                      <td className="px-3 py-2 text-center text-xs">{r.unit}</td>
                      <td className="px-3 py-2 text-right">
                        <input data-testid={`inv-qty-${r.item_id}`} type="number" step="0.01" disabled={locked}
                          value={val} onChange={(e) => setQty({ ...qty, [r.item_id]: e.target.value })}
                          className="w-24 rounded-lg border border-[#E3DED4] px-2 py-1 text-right tabular text-sm outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
                      </td>
                      <td className="px-3 py-2 text-right tabular text-[#6F6A60]">{fmtEuroCents(r.price)}</td>
                      <td className="px-3 py-2 text-right tabular font-semibold">{fmtEuroCents((Number(val) || 0) * Number(r.price || 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
